import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { apiError } from "@/api";
import { useAuth } from "@/context/AuthContext";
import { DataTable } from "@/components/DataTable";
import { PageHeader } from "@/components/PageHeader";
import { Badge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtIDR } from "@/lib/format";
import { Plus, Check, XCircle, History, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const SUB_STATUS = {
  pending: { label: "Menunggu Approval", cls: "bg-amber-50 text-amber-800 border-amber-300" },
  approved: { label: "Disetujui", cls: "bg-emerald-50 text-emerald-800 border-emerald-300" },
  rejected: { label: "Ditolak", cls: "bg-rose-50 text-rose-700 border-rose-300" },
};

export default function Substitution() {
  const { event, eventId } = useOutletContext();
  const { user } = useAuth();
  const [subs, setSubs] = useState([]);
  const [adjustments, setAdjustments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [open, setOpen] = useState(false);
  const [hist, setHist] = useState(null);
  const emptyForm = { order_id: "", item_index: "", new_catalog_item_id: "", new_name: "", new_sell_price: 0, reason: "" };
  const [form, setForm] = useState(emptyForm);
  const canCreate = ["sales", "shopper", "admin"].some((r) => user.roles.includes(r));
  const canApprove = ["ops", "admin"].some((r) => user.roles.includes(r));
  const canSettle = ["finance", "admin"].some((r) => user.roles.includes(r));

  const load = async () => {
    const [s, a, o, c] = await Promise.all([
      api.get(`/events/${eventId}/substitutions`), api.get(`/events/${eventId}/adjustments`), api.get(`/orders?event_id=${eventId}`), api.get(`/events/${eventId}/catalog`),
    ]);
    setSubs(s.data); setAdjustments(a.data); setOrders(o.data); setCatalog(c.data);
  };
  useEffect(() => { load(); }, [eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedOrder = orders.find((o) => o.id === form.order_id);
  const oldItem = selectedOrder && form.item_index !== "" ? selectedOrder.items[Number(form.item_index)] : null;
  const previewDiff = oldItem ? (Number(form.new_sell_price) - oldItem.sell_price) * oldItem.qty : 0;

  const pickCatalog = (id) => {
    const it = catalog.find((c) => c.id === id);
    setForm({ ...form, new_catalog_item_id: id, new_name: it ? it.name : form.new_name, new_sell_price: it ? it.sell_price : form.new_sell_price });
  };

  const create = async () => {
    if (!form.order_id || form.item_index === "") return toast.error("Pilih order & item");
    if (!form.new_name) return toast.error("Pilih item katalog pengganti atau isi nama");
    try {
      await api.post("/substitutions", { ...form, new_catalog_item_id: form.new_catalog_item_id || null, item_index: Number(form.item_index), new_sell_price: Number(form.new_sell_price) });
      toast.success("Substitusi dibuat — menunggu approval Ops/Admin"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const approve = async (sid, role, approved) => {
    try { await api.patch(`/substitutions/${sid}/approve`, { role, approved }); toast.success("Approval tersimpan"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const settle = async (aid) => {
    try { await api.patch(`/adjustments/${aid}/settle`); toast.success("Adjustment diselesaikan"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const subCols = [
    { key: "customer_name", label: "Customer", render: (r) => <span>{r.customer_name || "-"}<div className="text-[10px] font-mono text-slate-400">{r.order_id.slice(0, 8)}</div></span> },
    { key: "old_name", label: "Item Asal", render: (r) => <span className="line-through text-slate-500">{r.old_name}{r.old_sku && <span className="ml-1 text-[10px] font-mono no-underline">{r.old_sku}</span>}</span> },
    { key: "new_name", label: "Item Pengganti", render: (r) => (
      <div className="flex items-center gap-1.5"><ArrowRight className="w-3.5 h-3.5 text-slate-300" /><span className="font-semibold text-purple-700">{r.new_name}</span>
        {r.new_catalog_item_id ? <span className="text-[10px] font-mono text-slate-400" title="Terhubung ke katalog">{r.new_sku || "katalog"}</span> : <span className="text-[10px] text-amber-600">manual</span>}</div>
    ) },
    { key: "qty", label: "Qty", mono: true },
    { key: "price_diff", label: "Selisih", mono: true, render: (r) => (
      <span className={r.price_diff > 0 ? "text-rose-600 font-semibold" : r.price_diff < 0 ? "text-emerald-600 font-semibold" : ""}>{fmtIDR(r.price_diff)}</span>
    ), raw: (r) => r.price_diff },
    { key: "adjustment_type", label: "Tipe", render: (r) => r.adjustment_type === "none" ? "—" : <Badge label={r.adjustment_type === "top_up" ? "Top-up" : "Refund"} cls={r.adjustment_type === "top_up" ? "bg-rose-50 text-rose-700 border-rose-300" : "bg-emerald-50 text-emerald-700 border-emerald-300"} /> },
    { key: "sales_approved", label: "Approval", sortable: false, render: (r) => r.sales_approved ? <Check className="w-4 h-4 text-emerald-600" /> : <span className="text-slate-300">—</span> },
    { key: "status", label: "Status", render: (r) => { const s = SUB_STATUS[r.status]; return <Badge label={s.label} cls={s.cls} />; }, raw: (r) => r.status },
    { key: "actions", label: "Aksi", sortable: false, noExport: true, render: (r) => (
      <div className="flex items-center gap-1">
        <button data-testid={`sub-history-${r.id.slice(0,8)}`} className="p-1.5 text-slate-400 hover:text-slate-700" onClick={() => setHist(r)}><History className="w-4 h-4" /></button>
        {r.status === "pending" && canApprove && <Button size="sm" className="h-7 text-xs bg-blue-600" data-testid={`approve-sub-${r.id.slice(0,8)}`} onClick={() => approve(r.id, "ops", true)}>Setujui</Button>}
        {r.status === "pending" && canApprove && <button data-testid={`reject-sub-${r.id.slice(0,8)}`} className="p-1.5 text-rose-400 hover:text-rose-600" onClick={() => approve(r.id, "ops", false)}><XCircle className="w-4 h-4" /></button>}
      </div>
    ) },
  ];

  const adjCols = [
    { key: "type", label: "Tipe", render: (r) => <Badge label={r.type === "top_up" ? "Top-up (kurang bayar)" : "Refund"} cls={r.type === "top_up" ? "bg-rose-50 text-rose-700 border-rose-300" : "bg-emerald-50 text-emerald-700 border-emerald-300"} /> },
    { key: "amount", label: "Nominal", mono: true, render: (r) => <span className="font-semibold">{fmtIDR(r.amount)}</span>, raw: (r) => r.amount },
    { key: "customer_name", label: "Customer / Order", render: (r) => <span>{r.customer_name || "-"}<div className="text-[10px] font-mono text-slate-400">{r.order_id.slice(0, 8)}</div></span> },
    { key: "old_name", label: "Perubahan", render: (r) => r.old_name ? <span className="text-xs"><span className="line-through text-slate-400">{r.old_name}</span> → <span className="font-semibold text-purple-700">{r.new_name}</span>{r.qty ? <span className="text-slate-400"> ×{r.qty}</span> : null}</span> : <span className="text-slate-400">—</span> },
    { key: "settled", label: "Status", render: (r) => r.settled ? <Badge label="Selesai" cls="bg-emerald-50 text-emerald-700 border-emerald-300" /> : <Badge label="Belum diselesaikan" cls="bg-amber-50 text-amber-800 border-amber-300" />, raw: (r) => r.settled },
    { key: "actions", label: "", sortable: false, noExport: true, render: (r) => !r.settled && canSettle && <Button size="sm" className="h-8" data-testid={`settle-adj-${r.id.slice(0,8)}`} onClick={() => settle(r.id)}>Selesaikan</Button> },
  ];

  return (
    <div className="space-y-6">
      <div>
        <PageHeader title="Substitusi Barang" subtitle="Substitusi wajib refer ke item asal. Order lama tetap aktif & ditandai ada perubahan. Approval oleh Ops (Sales & Shopper) atau Admin.">
          {canCreate && <Button data-testid="btn-create-substitution" onClick={() => { setForm(emptyForm); setOpen(true); }} className="gap-1.5 bg-slate-900 hover:bg-slate-800"><Plus className="w-4 h-4" /> Buat Substitusi</Button>}
        </PageHeader>
        <DataTable testId="substitution-table" columns={subCols} data={subs} searchKeys={["old_name", "new_name", "status"]} exportName={`substitusi-${event?.code}`} emptyText="Belum ada substitusi." />
      </div>

      <div>
        <h2 className="text-lg font-bold tracking-tight text-slate-900 mb-3">Adjustment (Refund / Top-up)</h2>
        <DataTable testId="adjustment-table" columns={adjCols} data={adjustments} searchKeys={["type"]} exportName={`adjustment-${event?.code}`} emptyText="Belum ada adjustment." />
      </div>

      {/* create dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Buat Substitusi</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Order</Label>
              <Select value={form.order_id} onValueChange={(v) => setForm({ ...form, order_id: v, item_index: "" })}>
                <SelectTrigger data-testid="sub-order"><SelectValue placeholder="Pilih order" /></SelectTrigger>
                <SelectContent>{orders.map((o) => <SelectItem key={o.id} value={o.id}>{o.id.slice(0, 8)} · {o.customer_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {selectedOrder && (
              <div><Label>Item Asal</Label>
                <Select value={String(form.item_index)} onValueChange={(v) => { const it = selectedOrder.items[Number(v)]; setForm({ ...form, item_index: v, new_catalog_item_id: "", new_name: "", new_sell_price: it.sell_price }); }}>
                  <SelectTrigger data-testid="sub-item"><SelectValue placeholder="Pilih item" /></SelectTrigger>
                  <SelectContent>{selectedOrder.items.map((i, idx) => <SelectItem key={`${i.catalog_item_id}-${idx}`} value={String(idx)}>{i.name} (x{i.qty}) · {fmtIDR(i.sell_price)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {oldItem && (
              <div><Label>Item Pengganti dari Katalog</Label>
                <Select value={form.new_catalog_item_id} onValueChange={pickCatalog}>
                  <SelectTrigger data-testid="sub-new-catalog"><SelectValue placeholder="Pilih item katalog (opsional)" /></SelectTrigger>
                  <SelectContent>{catalog.filter((c) => c.id !== oldItem.catalog_item_id).map((c) => <SelectItem key={c.id} value={c.id}>{c.sku ? `${c.sku} · ` : ""}{c.name} · {fmtIDR(c.sell_price)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div><Label>Nama Item Pengganti</Label><Input data-testid="sub-new-name" value={form.new_name} onChange={(e) => setForm({ ...form, new_name: e.target.value })} placeholder="Terisi otomatis dari katalog, bisa diubah" /></div>
            <div><Label>Harga Jual Pengganti (IDR)</Label><Input data-testid="sub-new-price" type="number" value={form.new_sell_price} onChange={(e) => setForm({ ...form, new_sell_price: e.target.value })} /></div>
            {oldItem && (
              <div data-testid="sub-preview" className={`text-sm rounded-md border px-3 py-2 ${previewDiff > 0 ? "bg-rose-50 border-rose-200 text-rose-700" : previewDiff < 0 ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-slate-50 border-slate-200 text-slate-600"}`}>
                {previewDiff > 0 ? `Customer perlu top-up ${fmtIDR(previewDiff)}` : previewDiff < 0 ? `Customer akan di-refund ${fmtIDR(-previewDiff)}` : "Harga sama — tidak ada adjustment"} (qty {oldItem.qty}, setelah disetujui)
              </div>
            )}
            <div><Label>Alasan</Label><Input value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} placeholder="Out of stock, dll" /></div>
          </div>
          <DialogFooter><Button data-testid="save-substitution" onClick={create} className="bg-slate-900 hover:bg-slate-800">Buat</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* history dialog */}
      <Dialog open={!!hist} onOpenChange={(v) => !v && setHist(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Histori Perubahan</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {hist?.history?.map((h, i) => (
              <div key={`${h.at}-${h.action}-${i}`} className="flex items-start gap-2 text-sm border-l-2 border-slate-200 pl-3 py-1">
                <div><span className="font-semibold">{h.by}</span> <span className="text-slate-500">{h.action}</span>{h.detail && <div className="text-xs text-slate-400">{h.detail}</div>}<div className="text-[10px] text-slate-400 font-mono">{new Date(h.at).toLocaleString("id-ID")}</div></div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
